import React, { useState, useMemo } from 'react';
import Papa from 'papaparse';
import { Icons } from '../ui/Icons';
import type { ParkingReservation, ReservationSource } from '../../types';
import Modal from '../ui/Modal';
import ConfirmationModal from '../ui/ConfirmationModal';
import CSVImportModal from './CSVImportModal';
import toast from 'react-hot-toast';
import { 
    applyFilters, 
    calculateCounts, 
    FilterType, 
    normalizeStatus, 
    getTodayString,
    isReserved,
    isCheckedIn,
    isDueToday,
    isOverdue,
    isFutureDeparture,
    isReservationInactive,
    getSystemStatus,
    isCheckedOutStatus
} from '../../lib/reservationFilters';

const calculateDays = (start: string, end: string) => {
    if (!start || !end) return '1';
    const [sYear, sMonth, sDay] = start.split('-').map(Number);
    const [eYear, eMonth, eDay] = end.split('-').map(Number);
    const s = new Date(sYear, sMonth - 1, sDay);
    const e = new Date(eYear, eMonth - 1, eDay);
    const diffTime = e.getTime() - s.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays.toString();
};

interface ParkingReservationViewProps {
    reservations: ParkingReservation[];
    reservationSources: ReservationSource[];
    parkingCapacity: number;
    onSaveReservation: (reservation: Omit<ParkingReservation, 'id'> & { id?: string }) => void;
    onImportReservations: (reservations: ParkingReservation[]) => void;
    onDeleteReservation: (id: string) => void;
    onInitiatePrint: (reservation: ParkingReservation) => void;
    onInitiateBulkPrint: (date: string) => void;
}

const ParkingReservationView: React.FC<ParkingReservationViewProps> = ({
    reservations,
    reservationSources,
    parkingCapacity,
    onSaveReservation,
    onImportReservations,
    onDeleteReservation,
    onInitiatePrint,
    onInitiateBulkPrint
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [bulkPrintDate, setBulkPrintDate] = useState(new Date().toISOString().split('T')[0]);
    const [showInactive, setShowInactive] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingReservation, setEditingReservation] = useState<ParkingReservation | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [reservationToDelete, setReservationToDelete] = useState<string | null>(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewReservation, setPreviewReservation] = useState<ParkingReservation | null>(null);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortColumn, setSortColumn] = useState<keyof ParkingReservation>('startDate');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [activeFilter, setActiveFilter] = useState<FilterType>('All');
    const ITEMS_PER_PAGE = 10;

    const [formData, setFormData] = useState<Partial<ParkingReservation>>({
        reservationSource: '',
        confirmationNumber: '',
        numberOfPeople: '1',
        authorizedBy: 'Reception',
        vehicle_plate: '',
        guestName: '',
        roomNumber: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        hotelCheckInDate: '',
        hotelCheckOutDate: '',
        roomType: '',
        datesOfSleep: '',
        numberOfParkings: '1',
        numberOfRooms: '1',
        numberOfDays: '1',
        status: 'Reservation',
        eta: '',
        notes: '',
        customerPhone: '',
        customerEmail: '',
        total: '',
        carMakeModel: ''
    });

    const filterCounts = useMemo(() => {
        const activeReservations = showInactive ? reservations : reservations.filter(res => !isReservationInactive(res));
        return calculateCounts(activeReservations);
    }, [reservations, showInactive]);

    const filteredReservations = useMemo(() => {
        let baseFiltered = applyFilters(reservations, activeFilter, searchTerm);

        return baseFiltered.filter(res => {
            const inactive = isReservationInactive(res);
            // Inactive filter should apply to all views unless explicitly showing inactive
            if (!showInactive && inactive) {
                return false;
            }
            return true;
        }).sort((a, b) => {
            // If sorting by Today Remaining, it's already sorted by ETA in applyFilters
            if (activeFilter === 'Today Remaining' && sortColumn === 'startDate') {
                return 0; // Keep the ETA sort
            }

            let valA: any = a[sortColumn];
            let valB: any = b[sortColumn];

            if (sortColumn === 'startDate' || sortColumn === 'endDate' || sortColumn === 'createdAt' || sortColumn === 'hotelCheckInDate' || sortColumn === 'hotelCheckOutDate') {
                valA = valA ? new Date(valA as string).getTime() : 0;
                valB = valB ? new Date(valB as string).getTime() : 0;
            } else if (typeof valA === 'string' && typeof valB === 'string') {
                valA = valA.toLowerCase();
                valB = valB.toLowerCase();
            }

            if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
            if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [reservations, searchTerm, showInactive, sortColumn, sortDirection, activeFilter]);

    const totalPages = Math.ceil(filteredReservations.length / ITEMS_PER_PAGE);
    const paginatedReservations = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredReservations.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredReservations, currentPage]);

    // Reset to first page when search or filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, showInactive, sortColumn, sortDirection, activeFilter]);

    const handleSort = (column: keyof ParkingReservation) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const renderSortIcon = (column: keyof ParkingReservation) => {
        if (sortColumn !== column) return <Icons.ChevronDown className="w-3 h-3 opacity-20" />;
        return sortDirection === 'asc' ? <Icons.ChevronUp className="w-3 h-3 text-indigo-500" /> : <Icons.ChevronDown className="w-3 h-3 text-indigo-500" />;
    };

    const handleOpenPreview = (reservation: ParkingReservation) => {
        setPreviewReservation(reservation);
        setIsPreviewModalOpen(true);
    };

    const handleUpdateStatus = (reservation: ParkingReservation, newStatus: ParkingReservation['status']) => {
        const updatedReservation = {
            ...reservation,
            status: newStatus,
        };
        onSaveReservation(updatedReservation);
        if (previewReservation?.id === reservation.id) {
            setPreviewReservation(updatedReservation);
        }
    };

    const handleOpenModal = (reservation?: ParkingReservation) => {
        if (reservation) {
            setEditingReservation(reservation);
            const days = calculateDays(reservation.startDate, reservation.endDate);
            setFormData({
                ...reservation,
                numberOfPeople: reservation.numberOfPeople === '0' || !reservation.numberOfPeople ? '1' : reservation.numberOfPeople,
                roomNumber: reservation.roomNumber || '0',
                numberOfParkings: reservation.numberOfParkings === '0' || !reservation.numberOfParkings ? '1' : reservation.numberOfParkings,
                numberOfRooms: reservation.numberOfRooms === '0' || !reservation.numberOfRooms ? '1' : reservation.numberOfRooms,
                numberOfDays: reservation.numberOfDays || days,
                vehicle_plate: reservation.vehicle_plate || '.',
                customerPhone: reservation.customerPhone || '',
                customerEmail: reservation.customerEmail || '',
                total: reservation.total || '',
                carMakeModel: reservation.carMakeModel || ''
            });
        } else {
            setEditingReservation(null);
            setFormData({
                reservationSource: '',
                confirmationNumber: '',
                numberOfPeople: '1',
                authorizedBy: 'Reception',
                vehicle_plate: '',
                guestName: '',
                roomNumber: '',
                startDate: new Date().toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                hotelCheckInDate: '',
                hotelCheckOutDate: '',
                roomType: '',
                datesOfSleep: '',
                numberOfParkings: '1',
                numberOfRooms: '1',
                numberOfDays: '1',
                status: 'Reservation',
                eta: '',
                notes: '',
                customerPhone: '',
                customerEmail: '',
                total: '',
                carMakeModel: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingReservation(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Generate 14-digit confirmation number if empty
        let confirmationNumber = formData.confirmationNumber;
        if (!confirmationNumber || confirmationNumber.trim() === '') {
            confirmationNumber = Math.floor(10000000000000 + Math.random() * 90000000000000).toString();
        }

        onSaveReservation({
            ...formData,
            vehicle_plate: (formData.vehicle_plate || '').replace(/\s+/g, '').toUpperCase() || '-',
            confirmationNumber,
            id: editingReservation?.id,
            createdAt: editingReservation?.createdAt || new Date().toISOString()
        } as any);
        handleCloseModal();
    };

    const confirmDelete = (id: string) => {
        setReservationToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = () => {
        if (reservationToDelete) {
            onDeleteReservation(reservationToDelete);
            setIsDeleteModalOpen(false);
            setReservationToDelete(null);
        }
    };

    const handleImportData = (newReservations: ParkingReservation[]) => {
        onImportReservations(newReservations);
    };

    const stats = useMemo(() => {
        const activeReservations = showInactive ? reservations : reservations.filter(res => !isReservationInactive(res));
        const counts = calculateCounts(activeReservations);
        
        const available = Math.max(0, parkingCapacity - counts['Checked In']);

        return {
            available,
            reserved: counts['Reserved'],
            checkedIn: counts['Checked In'],
            todayRemaining: counts['Today Remaining'],
            dueCheckOut: counts['Due Check-Out'],
            overdue: counts['Overdue Checkout']
        };
    }, [reservations, parkingCapacity, showInactive]);

    return (
        <div className="space-y-8 max-w-7xl mx-auto pb-12">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200 dark:shadow-none">
                            <Icons.Car className="w-6 h-6 text-white" />
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                            Parking Logistics
                        </h1>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium ml-12">
                        Real-time vehicle tracking and spot management
                    </p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700">
                        <Icons.Calendar className="w-4 h-4 text-slate-400 ml-2" />
                        <input 
                            type="date" 
                            value={bulkPrintDate}
                            onChange={(e) => setBulkPrintDate(e.target.value)}
                            className="bg-transparent border-none text-xs font-bold text-slate-600 dark:text-slate-300 focus:ring-0 p-1"
                        />
                        <button
                            onClick={() => onInitiateBulkPrint(bulkPrintDate)}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none"
                        >
                            <Icons.Printer className="w-3.5 h-3.5 mr-2" />
                            PRINT ALL BY DATE
                        </button>
                    </div>

                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
                        <button
                            onClick={() => setIsImportModalOpen(true)}
                            className="flex items-center px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all"
                        >
                            <Icons.Upload className="w-3.5 h-3.5 mr-2" />
                            IMPORT
                        </button>
                    </div>
                    
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex-1 lg:flex-none flex items-center justify-center px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none hover:scale-[1.02] active:scale-[0.98]"
                    >
                        <Icons.Plus className="w-4 h-4 mr-2" />
                        NEW RESERVATION
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {[
                    { label: 'AVAILABLE', value: stats.available, icon: Icons.ShieldCheck, color: 'blue', filter: 'All' },
                    { label: 'RESERVED', value: stats.reserved, icon: Icons.Calendar, color: 'indigo', filter: 'Reserved' },
                    { label: 'CHECKED IN', value: stats.checkedIn, icon: Icons.CheckCircle, color: 'emerald', filter: 'Checked In' },
                    { label: 'TODAY REMAINING', value: stats.todayRemaining, icon: Icons.Clock, color: 'sky', filter: 'Today Remaining' },
                    { label: 'DUE TODAY', value: stats.dueCheckOut, icon: Icons.LogOut, color: 'amber', filter: 'Due Check-Out' },
                    { label: 'OVERDUE', value: stats.overdue, icon: Icons.AlertTriangle, color: 'rose', filter: 'Overdue Checkout' },
                ].map((stat, i) => (
                    <div 
                        key={i} 
                        onClick={() => stat.filter && setActiveFilter(stat.filter as FilterType)}
                        className="bg-white dark:bg-slate-800 p-5 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98] group"
                    >
                        <div className="flex justify-between items-start mb-3">
                            <div className={`p-2.5 rounded-2xl bg-${stat.color}-50 dark:bg-${stat.color}-900/20 text-${stat.color}-600 dark:text-${stat.color}-400 group-hover:scale-110 transition-transform`}>
                                <stat.icon className="w-5 h-5" />
                            </div>
                            <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 tracking-widest uppercase">{stat.label}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <span className="text-3xl font-black text-slate-900 dark:text-white">{stat.value}</span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">Vehicles</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Advanced Filters */}
            <div className="bg-white dark:bg-slate-800 p-2 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto hide-scrollbar">
                <div className="flex items-center gap-2 min-w-max">
                    {(Object.keys(filterCounts) as FilterType[]).map((filter) => (
                        <button
                            key={filter}
                            onClick={() => setActiveFilter(filter)}
                            className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                                activeFilter === filter 
                                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200 dark:shadow-none' 
                                    : 'bg-transparent text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                        >
                            {filter}
                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-black ${
                                activeFilter === filter 
                                    ? 'bg-white/20 text-white' 
                                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                            }`}>
                                {filterCounts[filter]}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                <div className="p-6 border-b border-slate-100 dark:border-slate-700/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="relative max-w-md w-full group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Icons.Search className="h-5 w-5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by spot, plate, name, or room..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-900/50 border-none rounded-2xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm font-medium"
                        />
                    </div>

                    <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-900/50 px-4 py-2 rounded-2xl border border-slate-100 dark:border-slate-800">
                        <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">Show Inactive</span>
                        <button
                            onClick={() => setShowInactive(!showInactive)}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showInactive ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-700'}`}
                        >
                            <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${showInactive ? 'translate-x-5' : 'translate-x-0'}`}
                            />
                        </button>
                    </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100 dark:divide-slate-700/50">
                        <thead className="bg-slate-50/50 dark:bg-slate-900/30 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th scope="col" className="px-4 py-4 text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors" onClick={() => handleSort('reservationSource')}>
                                    <div className="flex items-center gap-1">Source & Conf # {renderSortIcon('reservationSource')}</div>
                                </th>
                                <th scope="col" className="px-4 py-4 text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors" onClick={() => handleSort('guestName')}>
                                    <div className="flex items-center gap-1">Guest Info {renderSortIcon('guestName')}</div>
                                </th>
                                <th scope="col" className="px-4 py-4 text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors" onClick={() => handleSort('vehicle_plate')}>
                                    <div className="flex items-center gap-1">Vehicle {renderSortIcon('vehicle_plate')}</div>
                                </th>
                                <th scope="col" className="px-4 py-4 text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors" onClick={() => handleSort('startDate')}>
                                    <div className="flex items-center gap-1">Arrival {renderSortIcon('startDate')}</div>
                                </th>
                                <th scope="col" className="px-4 py-4 text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors" onClick={() => handleSort('endDate')}>
                                    <div className="flex items-center gap-1">Departure {renderSortIcon('endDate')}</div>
                                </th>
                                <th scope="col" className="px-4 py-4 text-left text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] cursor-pointer hover:text-slate-600 dark:hover:text-slate-300 transition-colors" onClick={() => handleSort('status')}>
                                    <div className="flex items-center gap-1">Status {renderSortIcon('status')}</div>
                                </th>
                                <th scope="col" className="sticky right-0 z-10 bg-slate-50 dark:bg-slate-900 px-4 py-4 text-right text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] border-l border-slate-100 dark:border-slate-700/50">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/50">
                            {paginatedReservations.length > 0 ? (
                                paginatedReservations.map((res) => {
                                    const systemStatus = getSystemStatus(res);
                                    const isInactive = isReservationInactive(res);
                                    const isNoShow = systemStatus === 'NO SHOW';
                                    const isOverdueVal = systemStatus === 'OVERDUE';
                                    const isDueTodayVal = systemStatus === 'DUE TODAY';
                                    const isUpcoming = (normalizeStatus(res.status) === 'reservation' || normalizeStatus(res.status) === 'reserved') && (res.startDate === getTodayString() || res.hotelCheckInDate === getTodayString());

                                    return (
                                        <tr key={res.id} className={`group hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-all ${
                                            isOverdueVal ? 'bg-rose-50/30 dark:bg-rose-900/10' : 
                                            isNoShow ? 'bg-amber-50/30 dark:bg-amber-900/10' :
                                            isUpcoming ? 'bg-blue-50/30 dark:bg-blue-900/10' :
                                            isDueTodayVal ? 'bg-amber-50/30 dark:bg-amber-900/10' : ''
                                        } ${isInactive ? 'opacity-60 grayscale-[0.3]' : ''}`}>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-xl ${isOverdueVal ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600' : isDueTodayVal ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                                                        {isOverdueVal ? <Icons.AlertTriangle className="w-4 h-4 animate-pulse" /> : isDueTodayVal ? <Icons.Clock className="w-4 h-4" /> : <Icons.Hash className="w-4 h-4" />}
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">{res.reservationSource}</div>
                                                        <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 tracking-widest">#{res.confirmationNumber}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold text-xs">
                                                        {res.guestName.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-bold text-slate-900 dark:text-white">{res.guestName}</div>
                                                        <div className="text-[10px] font-black text-indigo-600/60 dark:text-indigo-400/60 uppercase tracking-widest flex items-center gap-1">
                                                            <Icons.Home className="w-3 h-3" /> {(res.roomNumber || '0').toString().replace(/^R/i, '')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex flex-col gap-1">
                                                    <div className="inline-flex items-center px-3 py-1 rounded-lg text-[11px] font-black font-mono bg-slate-900 text-white dark:bg-slate-700 tracking-[0.15em] shadow-sm">
                                                        {res.vehicle_plate || '.'}
                                                    </div>
                                                    {res.eta && (
                                                        <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1 ml-1">
                                                            <Icons.Clock className="w-3 h-3" /> {res.eta}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className={`text-xs font-bold ${isOverdueVal ? 'text-rose-600' : isDueTodayVal ? 'text-amber-600' : 'text-slate-700 dark:text-slate-300'}`}>
                                                    {res.startDate}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">
                                                    {res.endDate}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex flex-col gap-2">
                                                    <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest
                                                        ${isCheckedIn(res) ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 
                                                          isCheckedOutStatus(res.status) ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' : 
                                                          'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'}`}>
                                                        {res.status}
                                                    </span>
                                                    {systemStatus && (
                                                        <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[9px] font-black animate-pulse tracking-tighter
                                                            ${systemStatus === 'OVERDUE' ? 'bg-rose-600 text-white' : 
                                                              systemStatus === 'DUE TODAY' ? 'bg-amber-500 text-white' : 
                                                              'bg-amber-600 text-white'}`}>
                                                            {systemStatus}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="sticky right-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-900 px-4 py-4 whitespace-nowrap text-right border-l border-slate-100 dark:border-slate-700/50">
                                                <div className="flex justify-end gap-1">
                                                    <button onClick={() => handleOpenPreview(res)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-all" title="Preview">
                                                        <Icons.Eye className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => onInitiatePrint(res)} className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-xl transition-all" title="Print Voucher">
                                                        <Icons.Printer className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => handleOpenModal(res)} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-xl transition-all" title="Edit">
                                                        <Icons.Edit className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => confirmDelete(res.id)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all" title="Delete">
                                                        <Icons.Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                                            <Icons.Car className="w-12 h-12 mb-4 opacity-20" />
                                            <p className="text-sm font-bold">No parking reservations found.</p>
                                            <p className="text-xs mt-1">Try adjusting your search or filters.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden grid grid-cols-1 divide-y divide-slate-100 dark:divide-slate-700/50">
                    {paginatedReservations.length > 0 ? (
                        paginatedReservations.map((res) => (
                            <ReservationCard
                                key={res.id}
                                reservation={res}
                                onPreview={handleOpenPreview}
                                onEdit={handleOpenModal}
                                onDelete={confirmDelete}
                                onPrint={onInitiatePrint}
                                isLate={isOverdue(res)}
                                isExpiringToday={isDueToday(res)}
                            />
                        ))
                    ) : (
                        <div className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                                <Icons.Car className="w-12 h-12 mb-4 opacity-20" />
                                <p className="text-sm font-bold">No parking reservations found.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="px-6 py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredReservations.length)}</span> of <span className="font-medium">{filteredReservations.length}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                                    >
                                        <span className="sr-only">Previous</span>
                                        <Icons.ChevronLeft className="h-5 w-5" />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                currentPage === page
                                                    ? 'z-10 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                                    : 'bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                                    >
                                        <span className="sr-only">Next</span>
                                        <Icons.ChevronRight className="h-5 w-5" />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {isImportModalOpen && (
                <CSVImportModal
                    isOpen={isImportModalOpen}
                    onClose={() => setIsImportModalOpen(false)}
                    onImport={handleImportData}
                    existingReservations={reservations}
                    reservationSources={reservationSources}
                />
            )}

            {isModalOpen && (
                <Modal onClose={handleCloseModal} title={editingReservation ? "Edit Reservation" : "New Parking Reservation"} maxWidthClass="sm:max-w-4xl">
                    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-950">
                        <div className="p-6 space-y-8">
                            {/* Section: Guest & Booking Information */}
                            <section>
                                <div className="flex items-center gap-2 mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">
                                    <Icons.User className="w-4 h-4 text-indigo-600" />
                                    <h3 className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-widest">Guest & Booking</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                    <div className="md:col-span-8">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Guest Full Name *</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Icons.User className="h-3.5 w-3.5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                            </div>
                                            <input
                                                type="text"
                                                required
                                                value={formData.guestName}
                                                onChange={(e) => setFormData({ ...formData, guestName: e.target.value })}
                                                className="block w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                                placeholder="e.g., John Smith"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Room Number *</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Icons.Home className="h-3.5 w-3.5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                            </div>
                                            <input
                                                type="text"
                                                required
                                                value={formData.roomNumber}
                                                onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value.replace(/^R/i, '') })}
                                                className="block w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                                placeholder="e.g., 402"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Source *</label>
                                        <select
                                            required
                                            value={formData.reservationSource}
                                            onChange={(e) => setFormData({ ...formData, reservationSource: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold appearance-none"
                                        >
                                            <option value="">Select Source</option>
                                            {reservationSources.map(source => (
                                                <option key={source.id} value={source.name}>{source.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Confirmation # (Auto-generated if empty)</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <Icons.Hash className="h-3.5 w-3.5 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                            </div>
                                            <input
                                                type="text"
                                                value={formData.confirmationNumber}
                                                onChange={(e) => setFormData({ ...formData, confirmationNumber: e.target.value })}
                                                className="block w-full pl-10 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                                placeholder="e.g., 87654321"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Guests *</label>
                                        <input
                                            type="number"
                                            required
                                            min="1"
                                            value={formData.numberOfPeople}
                                            onChange={(e) => setFormData({ ...formData, numberOfPeople: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Phone</label>
                                        <input
                                            type="text"
                                            value={formData.customerPhone}
                                            onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                            placeholder="Phone #"
                                        />
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Email</label>
                                        <input
                                            type="email"
                                            value={formData.customerEmail}
                                            onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                            placeholder="guest@example.com"
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Section: Vehicle & Parking Logistics */}
                            <section>
                                <div className="flex items-center gap-2 mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">
                                    <Icons.Car className="w-4 h-4 text-emerald-600" />
                                    <h3 className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-widest">Vehicle & Parking</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                    <div className="md:col-span-3">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">License Plate *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.vehicle_plate}
                                            onChange={(e) => setFormData({ ...formData, vehicle_plate: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-black uppercase tracking-widest"
                                            placeholder="ABC-1234"
                                        />
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Car Make/Model</label>
                                        <input
                                            type="text"
                                            value={formData.carMakeModel}
                                            onChange={(e) => setFormData({ ...formData, carMakeModel: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                            placeholder="e.g., Audi Q5"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Total</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">$</span>
                                            <input
                                                type="text"
                                                value={formData.total}
                                                onChange={(e) => setFormData({ ...formData, total: e.target.value })}
                                                className="block w-full pl-6 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-1">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Spots</label>
                                        <input
                                            type="text"
                                            value={formData.numberOfParkings || ''}
                                            onChange={(e) => setFormData({ ...formData, numberOfParkings: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">ETA</label>
                                        <input
                                            type="time"
                                            value={formData.eta || ''}
                                            onChange={(e) => setFormData({ ...formData, eta: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                        />
                                    </div>
                                </div>
                            </section>

                            {/* Section: Schedule & Status */}
                            <section>
                                <div className="flex items-center gap-2 mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">
                                    <Icons.Calendar className="w-4 h-4 text-amber-600" />
                                    <h3 className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-widest">Schedule & Status</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                    <div className="md:col-span-3">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Start Date *</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.startDate}
                                            onChange={(e) => {
                                                setFormData({ ...formData, startDate: e.target.value });
                                            }}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                        />
                                    </div>
                                    <div className="md:col-span-3">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">End Date *</label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.endDate}
                                            onChange={(e) => {
                                                setFormData({ ...formData, endDate: e.target.value });
                                            }}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Days</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={formData.numberOfDays || '1'}
                                            onChange={(e) => {
                                                setFormData({ ...formData, numberOfDays: e.target.value });
                                            }}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                        />
                                    </div>
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Status</label>
                                        <div className="flex p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 gap-1 overflow-x-auto">
                                            {['Reservation', 'Check-In', 'Check-Out'].map((status) => (
                                                <button
                                                    key={status}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, status: status as any })}
                                                    className={`flex-1 py-1.5 px-3 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all ${
                                                        formData.status === status
                                                            ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                                            : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 whitespace-nowrap'
                                                    }`}
                                                >
                                                    {status}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section: Additional Details */}
                            <section>
                                <div className="flex items-center gap-2 mb-4 border-b border-slate-100 dark:border-slate-800 pb-2">
                                    <Icons.FileText className="w-4 h-4 text-slate-400" />
                                    <h3 className="text-[11px] font-black text-slate-900 dark:text-white uppercase tracking-widest">Additional Details</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                    <div className="md:col-span-4">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Authorized By *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.authorizedBy}
                                            onChange={(e) => setFormData({ ...formData, authorizedBy: e.target.value })}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-semibold"
                                            placeholder="Manager Name"
                                        />
                                    </div>
                                    <div className="md:col-span-5">
                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Internal Notes</label>
                                        <textarea
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            rows={1}
                                            className="block w-full px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all text-sm font-medium"
                                            placeholder="Special requests..."
                                        />
                                    </div>
                                </div>
                            </section>
                        </div>
                        
                        <div className="p-6 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="px-6 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-8 py-2.5 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none"
                            >
                                {editingReservation ? 'Update Reservation' : 'Create Reservation'}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {isPreviewModalOpen && previewReservation && (
                <Modal onClose={() => setIsPreviewModalOpen(false)} title="Reservation Details" maxWidthClass="sm:max-w-3xl">
                    <div className="flex flex-col h-full bg-white dark:bg-slate-950">
                        {/* Hero Header */}
                        <div className="relative overflow-hidden p-6 border-b border-slate-100 dark:border-slate-800">
                            <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 rounded-full -mr-24 -mt-24 blur-3xl" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500/5 rounded-full -ml-16 -mb-16 blur-3xl" />
                            
                            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="hidden sm:flex p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200 dark:shadow-none text-white shrink-0">
                                        <Icons.User className="w-6 h-6" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.1em] ${
                                                isCheckedIn(previewReservation) 
                                                    ? 'bg-emerald-500 text-white'
                                                    : isCheckedOutStatus(previewReservation.status)
                                                    ? 'bg-slate-400 text-white'
                                                    : 'bg-indigo-500 text-white'
                                            }`}>
                                                {previewReservation.status}
                                            </span>
                                            {getSystemStatus(previewReservation) && (
                                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-[0.1em] animate-pulse ${
                                                    getSystemStatus(previewReservation) === 'OVERDUE' ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'
                                                }`}>
                                                    {getSystemStatus(previewReservation)}
                                                </span>
                                            )}
                                            <span className="text-[18px] font-mono font-bold text-slate-400 tracking-wider">
                                                #{previewReservation.confirmationNumber}
                                            </span>
                                        </div>
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                                            {previewReservation.guestName}
                                        </h3>
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                                            {previewReservation.reservationSource} • {previewReservation.roomType || 'Standard Room'}
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => onInitiatePrint(previewReservation)}
                                        className="p-2 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all shadow-sm"
                                        title="Print"
                                    >
                                        <Icons.Printer className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleOpenModal(previewReservation);
                                            setIsPreviewModalOpen(false);
                                        }}
                                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-widest rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none"
                                    >
                                        <Icons.Edit className="w-3.5 h-3.5" />
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (previewReservation) {
                                                setReservationToDelete(previewReservation.id);
                                                setIsDeleteModalOpen(true);
                                                setIsPreviewModalOpen(false);
                                            }
                                        }}
                                        className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all"
                                        title="Delete"
                                    >
                                        <Icons.Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Key Stats Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800/50">
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Parkings</p>
                                    <p className="text-xl font-black text-slate-900 dark:text-white">{previewReservation.numberOfParkings === '0' || !previewReservation.numberOfParkings ? '1' : previewReservation.numberOfParkings}</p>
                                </div>
                                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-800/50">
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Rooms</p>
                                    <p className="text-xl font-black text-slate-900 dark:text-white">{previewReservation.numberOfRooms === '0' || !previewReservation.numberOfRooms ? '1' : previewReservation.numberOfRooms}</p>
                                </div>
                                <div className="p-3 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/20">
                                    <p className="text-[9px] font-bold text-indigo-600/60 dark:text-indigo-400/60 uppercase tracking-wider mb-1">Days</p>
                                    <p className="text-xl font-black text-indigo-700 dark:text-indigo-300">{previewReservation.numberOfDays || calculateDays(previewReservation.startDate, previewReservation.endDate)}</p>
                                </div>
                                <div className="p-3 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/20">
                                    <p className="text-[9px] font-bold text-indigo-600/60 dark:text-indigo-400/60 uppercase tracking-wider mb-1">Room No.</p>
                                    <p className="text-xl font-black text-indigo-700 dark:text-indigo-300">{(previewReservation.roomNumber || '0').toString().replace(/^R/i, '')}</p>
                                </div>
                                <div className="p-3 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/20">
                                    <p className="text-[9px] font-bold text-emerald-600/60 dark:text-emerald-400/60 uppercase tracking-wider mb-1">Total</p>
                                    <p className="text-xl font-black text-emerald-700 dark:text-emerald-300">${previewReservation.total || '0.00'}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Details Column */}
                                <div className="space-y-6">
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-1 h-3 bg-indigo-500 rounded-full" />
                                            <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest">Logistics</h4>
                                        </div>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-500">
                                                    <Icons.Calendar className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Parking Schedule</p>
                                                    <p className="text-xs font-bold text-slate-900 dark:text-white">
                                                        {previewReservation.startDate} <Icons.ArrowRight className="inline w-3 h-3 mx-1 text-slate-300" /> {previewReservation.endDate}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-500">
                                                    <Icons.Clock className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Hotel Stay</p>
                                                    <p className="text-xs font-bold text-slate-900 dark:text-white">
                                                        {previewReservation.hotelCheckInDate || '---'} <Icons.ArrowRight className="inline w-3 h-3 mx-1 text-slate-300" /> {previewReservation.hotelCheckOutDate || '---'}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-500">
                                                    <Icons.Car className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Vehicle</p>
                                                    <p className="text-xs font-black text-indigo-600 dark:text-indigo-400 tracking-widest">
                                                        {previewReservation.vehicle_plate || '.'} <span className="text-slate-400 font-normal mx-2">|</span> {previewReservation.carMakeModel || 'Unknown'}
                                                    </p>
                                                </div>
                                            </div>
                                            {previewReservation.customerEmail && (
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-500">
                                                        <Icons.Mail className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Email</p>
                                                        <p className="text-xs font-bold text-slate-900 dark:text-white truncate max-w-[200px]">
                                                            {previewReservation.customerEmail}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                </div>

                                {/* Status Column */}
                                <div className="space-y-6">
                                    <section>
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="w-1 h-3 bg-amber-500 rounded-full" />
                                            <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest">Quick Actions</h4>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2">
                                            {['Reservation', 'Check-In', 'Check-Out'].map((status) => {
                                                const isActive = previewReservation.status === status;
                                                return (
                                                    <button
                                                        key={status}
                                                        onClick={() => handleUpdateStatus(previewReservation, status as any)}
                                                        className={`flex items-center justify-between px-4 py-3 rounded-xl text-xs font-bold transition-all ${
                                                            isActive
                                                                ? 'bg-indigo-600 text-white shadow-md'
                                                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        <span>{status}</span>
                                                        {isActive && <Icons.CheckCircle className="w-4 h-4" />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </section>
                                </div>
                            </div>

                            {previewReservation.notes && (
                                <section className="p-4 bg-amber-50/30 dark:bg-amber-900/5 border border-amber-100/50 dark:border-amber-900/10 rounded-2xl">
                                    <p className="text-[11px] font-bold text-amber-600/60 uppercase tracking-wider mb-2">Notes</p>
                                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-medium italic">
                                        "{previewReservation.notes}"
                                    </p>
                                </section>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 flex items-center justify-between border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                Created: {new Date(previewReservation.createdAt).toLocaleDateString()}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onInitiatePrint(previewReservation)}
                                    className="px-4 py-2 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-black text-[9px] uppercase tracking-widest border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-50 transition-all"
                                >
                                    Print Ticket
                                </button>
                                <button
                                    onClick={() => setIsPreviewModalOpen(false)}
                                    className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-black text-[9px] uppercase tracking-widest rounded-xl hover:opacity-90 transition-all"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {isDeleteModalOpen && (
                <ConfirmationModal
                    title="Delete Reservation"
                    message="Are you sure you want to delete this parking reservation? This action cannot be undone."
                    confirmButtonText="Delete"
                    onConfirm={handleDelete}
                    onCancel={() => {
                        setIsDeleteModalOpen(false);
                        setReservationToDelete(null);
                    }}
                />
            )}
        </div>
    );
};

export default ParkingReservationView;

interface ReservationCardProps {
    reservation: ParkingReservation;
    onPreview: (res: ParkingReservation) => void;
    onEdit: (res: ParkingReservation) => void;
    onDelete: (id: string) => void;
    onPrint: (res: ParkingReservation) => void;
    isLate: boolean;
    isExpiringToday: boolean;
}

const ReservationCard: React.FC<ReservationCardProps> = ({
    reservation,
    onPreview,
    onEdit,
    onDelete,
    onPrint,
    isLate,
    isExpiringToday
}) => {
    const systemStatus = getSystemStatus(reservation);
    const isNoShow = systemStatus === 'NO SHOW';
    const isOverdueVal = systemStatus === 'OVERDUE';
    const isDueTodayVal = systemStatus === 'DUE TODAY';
    const isUpcoming = (normalizeStatus(reservation.status) === 'reservation' || normalizeStatus(reservation.status) === 'reserved') && (reservation.startDate === getTodayString() || reservation.hotelCheckInDate === getTodayString());

    return (
        <div className={`p-4 group relative transition-all ${
            isOverdueVal ? 'bg-rose-50/30 dark:bg-rose-900/10' : 
            isNoShow ? 'bg-amber-50/30 dark:bg-amber-900/10' :
            isUpcoming ? 'bg-blue-50/30 dark:bg-blue-900/10' :
            isDueTodayVal ? 'bg-amber-50/30 dark:bg-amber-900/10' : 'bg-white dark:bg-slate-800'
        }`}>
            {/* Status Indicator Bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                isCheckedIn(reservation) ? 'bg-emerald-500' :
                isCheckedOutStatus(reservation.status) ? 'bg-slate-400' :
                isOverdueVal ? 'bg-rose-500' :
                'bg-indigo-500'
            }`} />

            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 font-black text-sm shadow-sm">
                        {reservation.roomNumber ? reservation.roomNumber.toString().replace(/^R/i, '') : '??'}
                    </div>
                    <div>
                        <h4 className="text-sm font-black text-slate-900 dark:text-white leading-tight">{reservation.guestName}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{reservation.reservationSource}</span>
                            <span className="text-[10px] font-mono text-slate-400">#{reservation.confirmationNumber}</span>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                        isCheckedIn(reservation) ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                        isCheckedOutStatus(reservation.status) ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' :
                        'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                    }`}>
                        {reservation.status}
                    </span>
                    {systemStatus && (
                        <span className={`text-[9px] font-black animate-pulse uppercase tracking-tighter ${
                            systemStatus === 'OVERDUE' ? 'text-rose-600' : 'text-amber-600'
                        }`}>
                            {systemStatus}
                        </span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-1">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Vehicle</p>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-slate-900 dark:text-white tracking-widest bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-lg border border-slate-200 dark:border-slate-700">
                            {reservation.vehicle_plate || '.'}
                        </span>
                    </div>
                </div>
                <div className="space-y-1 text-right">
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Stay Period</p>
                    <p className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                        {reservation.startDate} <Icons.ArrowRight className="inline w-2.5 h-2.5 mx-1 text-slate-300" /> {reservation.endDate}
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400">
                        <Icons.Users className="w-3 h-3" />
                        {reservation.numberOfPeople === '0' || !reservation.numberOfPeople ? '1' : reservation.numberOfPeople}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400">
                        <Icons.Car className="w-3 h-3" />
                        {reservation.numberOfParkings === '0' || !reservation.numberOfParkings ? '1' : reservation.numberOfParkings}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button onClick={() => onPreview(reservation)} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                        <Icons.Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => onPrint(reservation)} className="p-2 text-slate-400 hover:text-emerald-600 transition-colors">
                        <Icons.Printer className="w-4 h-4" />
                    </button>
                    <button onClick={() => onEdit(reservation)} className="p-2 text-slate-400 hover:text-amber-600 transition-colors">
                        <Icons.Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(reservation.id)} className="p-2 text-slate-400 hover:text-rose-600 transition-colors">
                        <Icons.Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};
